// src/pages/Services.js
// MODERNIZED: Nulek brand palette, charcoal (#222222/#2E2E2E), Barlow font
// PRESERVED: isMobile state, resize handler, services data, all slugs/routes, sticky mobile CTA

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Services.module.css';

const Services = () => {
  const [isMobile, setIsMobile] = useState(false);

  // resize handler
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    { id: 1, title: 'Planning and Scheduling',            slug: 'PlanningScheduling', description: 'End-to-end scheduling from EOI to closeout — ensuring clear, accurate, and defensible programmes for building, water, rail, and energy projects.',            icon: '📅' },
    { id: 2, title: 'Schedule Risk Analysis',             slug: 'ScheduleRiskAnalysis', description: 'Quantitative risk assessment using Monte Carlo (P20/P50/P80), sensitivity analysis, and contingency modelling for schedule resilience.',                    icon: '⚠️' },
    { id: 3, title: 'Time–Chainage Diagram Development',  slug: 'TimeChainage',  description: 'High-impact linear scheduling using TILOS & Turbo-Chart — visualising sequencing, access, and production for rail, roads, and utilities.',                    icon: '🗺️' },
    { id: 4, title: 'Power BI Dashboard & Reporting',      slug: 'Report',        description: 'Real-time EVM, portfolio, and lookahead dashboards that turn complex data into executive-ready insights.',                                                        icon: '📊' },
    { id: 5, title: 'Training & Capability Uplift',        slug: 'Training',      description: 'Practical courses in Planning, EVM, and SRA — tailored for engineers, planners, and project managers.',                                                          icon: '🎓' },
  ];

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Professional Project Services | Nulek Consulting Australia</title>
        <meta name="description" content="Expert project planning, Monte Carlo risk analysis, time-chainage development, and Power BI reporting for complex infrastructure projects." />
      </Helmet>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroPattern} />
        <div className={styles.heroContent}>
          <p className={styles.eyebrowLight}>What We Offer</p>
          <h1 className={`${styles.heroTitle} ${isMobile ? styles.heroTitleMobile : ''}`}>
            Expert Project Services
          </h1>
          <p className={`${styles.heroText} ${isMobile ? styles.heroTextMobile : ''}`}>
            We ensure your projects are delivered <strong>on time, on budget, and with excellence.</strong>
          </p>
          <Link to="/contact" className={styles.heroButton}>
            Book a Consultation
          </Link>
        </div>
      </section>

      {/* ── SERVICES GRID ── */}
      <section className={`${styles.servicesGrid} ${isMobile ? styles.servicesGridMobile : ''}`}>
        {services.map((service) => (
          <div key={service.id} className={styles.serviceCard}>
            <div className={styles.serviceIconWrapper}>{service.icon}</div>
            <div className={styles.serviceAccent} />
            <h3 className={`${styles.serviceTitle} ${isMobile ? styles.serviceTitleMobile : ''}`}>
              {service.title}
            </h3>
            <p className={`${styles.serviceText} ${isMobile ? styles.serviceTextMobile : ''}`}>
              {service.description}
            </p>
            <div className={styles.cardActions}>
              <Link to={`/services/${service.slug}/`} className={styles.learnMore}>
                Learn More <span>→</span>
              </Link>
              <Link to="/contact" className={styles.bookNow}>
                Book This Service
              </Link>
            </div>
          </div>
        ))}
      </section>

      {/* ── TRUST STRIP ── */}
      <section className={styles.trustStrip}>
        <p className={styles.trustText}>
          <span>✅</span> Trusted across infrastructure sectors &nbsp;|&nbsp;
          <span>⭐</span> Client-focused delivery &nbsp;|&nbsp;
          <span>📈</span> Proven outcomes on complex projects
        </p>
      </section>

      {/* ── FINAL CTA ── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaBg} />
        <div className={styles.ctaContent}>
          <p className={styles.eyebrowCentered}>Take the Next Step</p>
          <h2 className={`${styles.ctaTitle} ${isMobile ? styles.ctaTitleMobile : ''}`}>
            Ready to Strengthen Your Project Controls?
          </h2>
          <p className={`${styles.ctaText} ${isMobile ? styles.ctaTextMobile : ''}`}>
            Let our experts help you build certainty into every phase — from bid to closeout.
          </p>
          <Link to="/contact" className={styles.ctaButton}>
            Schedule Your Consultation
          </Link>
        </div>
      </section>

      {/* PRESERVED: sticky mobile CTA */}
      {isMobile && (
        <div className={styles.stickyCta}>
          <Link to="/contact" className={styles.stickyCtaButton}>
            Book Now
          </Link>
        </div>
      )}
    </div>
  );
};

export default Services;